import { BadgeCheck, Building2, FileSearch, Landmark, Network, ShieldCheck, UsersRound, Zap } from 'lucide-react'
import React from 'react'

const problemPoints = [
    'Customers struggle to identify the right loan product and suitable bank.',
    'DSAs manually collect information, compare bank policies, and coordinate with bankers.',
    'Bankers receive incomplete or mismatched cases.',
    'Communication happens through scattered calls, emails, and messaging applications.',
    'Customers remain unaware of their real-time application status.',
    'Wrong bank selection increases rejection rates and delays approvals.',
]

const stakeholders = [
    {
        title: 'Customer',
        description: 'Customers receive a transparent, trackable, and digitally managed loan experience with real-time visibility into their application progress.',
        Icon: UsersRound,
    },
    {
        title: 'DSA',
        description: 'DSAs can create leads, upload customer information, manage documents, analyze eligibility, route applications intelligently, and track every stage digitally.',
        Icon: Network,
    },
    {
        title: 'Banker',
        description: 'Bankers receive complete, structured, and policy-matched loan applications with all necessary information already organized.',
        Icon: Landmark,
    },
]

const leadSystemPoints = [
    'Digital lead creation',
    'Customer profiling',
    'Document management',
    'Real-time tracking',
    'Loan stage monitoring',
    'Automated notifications',
    'Workflow synchronization',
]

const bankPolicyPoints = [
    'CIBIL score requirements',
    'FOIR policies',
    'Income criteria',
    'Employment conditions',
    'Risk appetite',
    'Product preferences',
]

const analysisPoints = [
    'Customer profile',
    'Financial details',
    'CIBIL information',
    'Liabilities',
    'Income structure',
    'Eligibility parameters',
]

const impactPoints = [
    'Reduces rejection rates',
    'Improves approval probability',
    'Saves operational time',
    'Increases processing efficiency',
]

const rejectionPoints = [
    'Policy mismatches',
    'Eligibility gaps',
    'Financial limitations',
    'Rejection possibilities',
]

const syncPoints = [
    'Approves',
    'Rejects',
    'Queries',
    'Updates',
    'Processes',
]

const documentProblems = [
    'Scattered WhatsApp documents',
    'Unorganized Excel sheets',
    'Repeated follow-ups',
    'Disconnected communication channels',
]

const managementPoints = [
    'DSA performance tracking',
    'Team hierarchy management',
    'Payout management',
    'Operational analytics',
    'Employee monitoring',
    'Lead allocation systems',
    'Workflow reporting',
    'Billing and reconciliation modules',
]

const infrastructurePoints = [
    'Workflow automation',
    'Policy intelligence',
    'Centralized operations',
    'Real-time synchronization',
    'Transparent communication',
    'Structured lead routing',
]

const differencePoints = [
    'End-to-end digital loan workflow',
    'Intelligent bank policy matching',
    'Real-time loan tracking',
    'Centralized operational ecosystem',
    'Transparent eligibility analysis',
    'Automated workflow synchronization',
    'Integrated payout & performance systems',
    'Multi-stakeholder coordination platform',
    'Scalable fintech infrastructure',
    'Reduced rejection & faster processing',
]

const BulletGrid = ({ items }) => (
    <div className="about-bullet-grid">
        {items.map((item, index) => (
            <div
                className="about-bullet reveal-on-scroll about-reveal-card"
                style={{ "--about-item-delay": `${index * 55}ms` }}
                key={item}
            >

                <BadgeCheck size={18} />
                <span>{item}</span>
            </div>
        ))}
    </div>
)

const AboutSection = ({ eyebrow, title, children, accent = false }) => (
    <section className={`about-section ${accent ? 'about-section--accent' : ''}`}>
        <div className="container about-section__inner px-4 sm:px-8 xl:px-4">
            <div className="about-section__head reveal-on-scroll about-reveal-heading">
                {eyebrow && <p className="about-eyebrow">{eyebrow}</p>}
                <h2>{title}</h2>
            </div>
            <div className="about-section__body reveal-on-scroll about-reveal-body">{children}</div>
        </div>
    </section>
)


const About = () => {
    return (
        <>
            <header className="about-hero">
               <div className="container about-hero__inner px-4 sm:px-8 xl:px-4 reveal-on-scroll about-reveal-heading">

                    <p className="about-eyebrow">About Our Platform</p>
                    <h1>
    <span>Transforming the</span>
    <span className="about-hero__highlight">Loan Ecosystem</span>
    <span>Through Technology</span>
</h1>

                    <p>
                        India’s loan processing ecosystem has long operated through disconnected processes, manual coordination,
                        delayed communication, and fragmented workflows between customers, DSAs, and bankers.
                    </p>
                    <p>
                        Our platform creates a complete digital ecosystem that connects customers, DSAs, bankers,
                        branches, and financial institutions into one unified loan processing network.
                    </p>
                </div>
            </header>

            <main className="about-platform">
    <AboutSection eyebrow="Leadership" title="Founder & CEO">
        <p>
            Our founder, a retired banker and former Regional Head, brings decades of expertise in
            mortgages, underwriting, operations, and compliance.
        </p>

        <p>
            His vision of innovation, trust, and technology forms the foundation of KSDPL&apos;s SaaS
            solutions for DSAs. By leveraging his experience, we&apos;re building a transparent,
            efficient, and growth-oriented ecosystem for financial associates across India.
        </p>
    </AboutSection>

    <AboutSection title="A Complete Loan processing System">
        <p>
            Our software digitizes, synchronizes, and streamlines the entire loan acquisition journey from
            lead generation to final decision tracking. This is not just a CRM or a lead management software.
        </p>

                    <p>
                        It is a complete Loan processing System designed to modernize and harmonize the lending workflow.
                    </p>
                </AboutSection>

                <AboutSection title="The Problem We Solve" accent>
                    <p>Traditional loan processing is heavily dependent on manual operations. In the conventional system:</p>
                    <BulletGrid items={problemPoints} />
                    <p>
                        The result is an inefficient ecosystem with operational friction at every stage. Our platform
                        eliminates this fragmentation by bringing the entire workflow into one intelligent digital infrastructure.
                    </p>
                </AboutSection>

                <AboutSection title="A Unified Digital Loan Journey">
                    <p>
                        Our software creates a seamless and synchronized journey for every stakeholder involved in the lending process.
                    </p>
                    <div className="about-stakeholder-grid">
                        {stakeholders.map(({ title, description, Icon }, index) => (
    <article
        className="about-stakeholder-card reveal-on-scroll about-reveal-card"
        style={{ "--about-item-delay": `${index * 90}ms` }}
        key={title}
    >

                                <Icon size={24} />
                                <h3>{title}</h3>
                                <p>{description}</p>
                            </article>
                        ))}
                    </div>
                </AboutSection>

                <AboutSection title="Intelligent Lead Management System" accent>
                    <p>
                        The journey begins when a customer approaches a DSA for a loan requirement. The DSA can instantly
                        create a lead within the platform and digitally manage the customer profile through a structured workflow.
                    </p>
                    <BulletGrid items={leadSystemPoints} />
                </AboutSection>

                <AboutSection title="Smart Eligibility & Bank Suggestion Engine">
                    <p>
                        Different banks operate under different lending policies. Traditionally, DSAs manually compare
                        these policies, resulting in time loss and high rejection probability.
                    </p>
                    <div className="about-two-column">
                        <div>
                            <h3>Bank policy factors</h3>
                            <BulletGrid items={bankPolicyPoints} />
                        </div>
                        <div>
                            <h3>Customer analysis</h3>
                            <BulletGrid items={analysisPoints} />
                        </div>
                    </div>
                    <p>
                        Based on this analysis, the system automatically suggests only those banks whose policies match
                        the customer profile.
                    </p>
                    <BulletGrid items={impactPoints} />
                </AboutSection>

                <AboutSection title="Transparent Rejection Intelligence" accent>
                    <p>
                        Our software does not simply show which banks are eligible. It also explains why certain banks are
                        not suitable for the customer profile.
                    </p>
                    <BulletGrid items={rejectionPoints} />
                </AboutSection>

                <AboutSection title="Real-Time Synchronization Across Stakeholders">
                    <p>
                        Whenever a banker approves, rejects, queries, updates, or processes a loan application, the update
                        becomes visible instantly to the DSA, customer, and authorized operational teams.
                    </p>
                    <BulletGrid items={syncPoints} />
                </AboutSection>

                <AboutSection title="Digital Payment & Verification Layer" accent>
                    <p>
                        Customers can securely complete platform registration payments through QR-code-based digital payment
                        systems. Once payment is completed, verification status updates automatically within the platform.
                    </p>
                    <BulletGrid items={['Digital verification', 'Process authenticity', 'Operational transparency', 'Reduced manual dependency']} />
                </AboutSection>

                <AboutSection title="Centralized Document & Workflow Management">
                    <p>
                        All customer records, loan documents, status updates, communications, and operational activities are
                        securely managed within a centralized system.
                    </p>
                    <BulletGrid items={documentProblems} />
                    <p>Every process becomes structured, searchable, trackable, and operationally efficient.</p>
                </AboutSection>

                <AboutSection title="Performance, Payout & Team Management" accent>
                    <p>The platform is not limited to loan processing alone. It also includes:</p>
                    <BulletGrid items={managementPoints} />
                </AboutSection>

                <AboutSection title="A Scalable Financial Infrastructure">
                    <p>
                        We are building a scalable digital infrastructure layer for the lending industry, connecting customers,
                        DSAs, bankers, branches, and financial institutions into one harmonized network.
                    </p>
                    <BulletGrid items={infrastructurePoints} />
                </AboutSection>

                <section className="about-vision">
                    <div className="container about-vision__inner px-4 sm:px-8 xl:px-4">
                        <ShieldCheck size={28} />
                        <p className="about-eyebrow">Our Vision</p>
                        <h2>
                            To build India’s most intelligent and connected digital loan processing ecosystem where technology
                            simplifies, accelerates, and transforms the entire loan acquisition journey.
                        </h2>
                    </div>
                </section>

                <AboutSection title="What Makes Our Platform Different">
                    <BulletGrid items={differencePoints} />
                </AboutSection>

                <section className="about-final">
                    <div className="container about-final__inner px-4 sm:px-8 xl:px-4">
                        <Zap size={28} />
                        <h2>More Than Software</h2>
                        <p>
                            This is not just a software platform. This is a complete digital loan processing ecosystem
                            designed to modernize the future of lending operations.
                        </p>
                    </div>
                </section>
            </main>
        </>
    )
}

export default About
